/**
 * Página de teste para Music Player com Spotify SDK
 */

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useMusicMatch } from "@/hooks/useMusicMatch";
import { useBPM } from "@/hooks/useBPM";
import { useSpotifyPlayback } from "@/hooks/useSpotifyPlayback";
import { MusicPlayer } from "@/components/music/MusicPlayer";
import { Heart, Music, Play, Pause, SkipForward, SkipBack } from "lucide-react";
import type { MusicContext } from "@/services/music-match-service";

export default function MusicTest() {
  const [bpm, setBpm] = useState(150);
  const [mood, setMood] = useState<'energized' | 'calm' | 'motivated' | 'focused'>('motivated');
  const [autoSync, setAutoSync] = useState(true);

  // Spotify SDK integration
  const spotifyPlayback = useSpotifyPlayback();
  
  const {
    currentTrack,
    playlist,
    isPlaying,
    generatePlaylist,
    playTrack,
    pause,
    resume,
    next,
    previous,
  } = useMusicMatch('spotify', spotifyPlayback.play);

  const handleGeneratePlaylist = async () => {
    const context: MusicContext = {
      bpm,
      mood,
      timeOfDay: new Date().getHours() < 12 ? 'morning' : 
                 new Date().getHours() < 18 ? 'afternoon' : 'evening',
      weather: 'sunny',
      routeType: 'urban'
    };

    await generatePlaylist(context);
  };

  const handlePlayPause = async () => {
    // Use Spotify SDK if available
    if (spotifyPlayback.isReady) {
      if (spotifyPlayback.isPlaying) {
        await spotifyPlayback.pause();
      } else {
        await spotifyPlayback.resume();
      }
      return;
    }

    // Fallback to preview player
    if (isPlaying) {
      await pause();
    } else if (currentTrack) {
      await resume();
    } else if (playlist.length > 0) {
      await playTrack(playlist[0]);
    }
  };

  const handleNext = async () => {
    if (spotifyPlayback.isReady) {
      await spotifyPlayback.next();
    } else {
      await next();
    }
  };

  const handlePrevious = async () => {
    if (spotifyPlayback.isReady) {
      await spotifyPlayback.previous();
    } else {
      await previous();
    }
  };

  const isSynced = currentTrack 
    ? Math.abs(bpm - (currentTrack.bpm || 0)) <= 10 
    : false;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted p-4">
      <div className="max-w-2xl mx-auto space-y-6 pt-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold flex items-center justify-center gap-3">
            <Music className="w-10 h-10 text-primary" />
            Music Player Test
          </h1>
          <p className="text-muted-foreground">
            Teste o player de música com Spotify SDK
          </p>
        </div>

        {/* Spotify Status */}
        {spotifyPlayback.isReady && (
          <Card className="bg-green-500/10 border-green-500/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-500">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="font-medium">Spotify SDK Conectado</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Controles</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* BPM Slider */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                BPM: {bpm}
              </label>
              <Slider
                value={[bpm]}
                onValueChange={(v) => setBpm(v[0])}
                min={80}
                max={200}
                step={5}
              />
            </div>

            {/* Mood Buttons */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mood:</label>
              <div className="grid grid-cols-2 gap-2">
                {(['energized', 'calm', 'motivated', 'focused'] as const).map((m) => (
                  <Button
                    key={m}
                    variant={mood === m ? 'default' : 'outline'}
                    onClick={() => setMood(m)}
                    className="capitalize"
                  >
                    {m}
                  </Button>
                ))}
              </div>
            </div>

            {/* Generate Playlist */}
            <Button
              onClick={handleGeneratePlaylist}
              className="w-full"
              size="lg"
            >
              <Music className="w-5 h-5 mr-2" />
              Gerar Playlist
            </Button>
          </CardContent>
        </Card>

        {/* Music Player */}
        <MusicPlayer
          currentTrack={spotifyPlayback.isReady ? spotifyPlayback.currentTrack : currentTrack}
          isPlaying={spotifyPlayback.isReady ? spotifyPlayback.isPlaying : isPlaying}
          currentBPM={bpm}
          autoSync={autoSync}
          isSynced={isSynced}
          onPlayPause={handlePlayPause}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onAutoSyncToggle={setAutoSync}
          spotifyReady={spotifyPlayback.isReady}
          position={spotifyPlayback.position}
          duration={spotifyPlayback.duration}
        />

        {/* Playlist */}
        {playlist.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Playlist ({playlist.length} músicas)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {playlist.map((track, index) => (
                  <div
                    key={track.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      currentTrack?.id === track.id
                        ? 'bg-primary/20 border border-primary'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    onClick={() => playTrack(track)}
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-primary to-accent rounded flex items-center justify-center">
                      {currentTrack?.id === track.id && isPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                      ) : (
                        <Play className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{track.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{track.artist}</p>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {track.bpm} BPM
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
